import { useRef, useEffect, useCallback } from 'react';
export function useLocalStoragePersistence({ storageKey, serialize, restore, onAfterRestore, isReady, resetKey }) {
    const restoredRef = useRef(false);
    const saveDebounceRef = useRef(null);
    const hasItemsRef = useRef(false);
    const lastResetKeyRef = useRef(resetKey);
    useEffect(() => {
        const resetKeyChanged = lastResetKeyRef.current !== resetKey && lastResetKeyRef.current !== undefined;
        lastResetKeyRef.current = resetKey;
        if (resetKeyChanged) {
            restoredRef.current = false;
            hasItemsRef.current = false;
        }
        if (restoredRef.current || !isReady)
            return;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.length > 0) {
                    restore(parsed);
                    hasItemsRef.current = true;
                    if (onAfterRestore) {
                        setTimeout(onAfterRestore, 0);
                    }
                }
            }
            catch (_a) {
                // Ignore parse errors
            }
        }
        restoredRef.current = true;
    }, [restore, storageKey, isReady, onAfterRestore, resetKey]);
    const saveToStorage = useCallback(() => {
        if (!restoredRef.current)
            return;
        if (saveDebounceRef.current) {
            clearTimeout(saveDebounceRef.current);
        }
        saveDebounceRef.current = setTimeout(() => {
            const serialized = serialize();
            localStorage.setItem(storageKey, JSON.stringify(serialized));
        }, 500);
    }, [serialize, storageKey]);
    useEffect(() => {
        return () => {
            if (saveDebounceRef.current) {
                clearTimeout(saveDebounceRef.current);
            }
        };
    }, []);
    return {
        isRestored: restoredRef.current,
        saveToStorage,
        hasRestoredItems: hasItemsRef.current
    };
}
